// AllBlogs.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './lisachar.css';
import { 
  db, 
  collection, 
  addDoc, 
  query, 
  orderBy, 
  onSnapshot, 
  serverTimestamp, 
  doc,
  getDocs
} from './firebase';

// Placeholder images for slides
import Lisa1 from './Images/Lis.png';
import por2 from './Images/por2.jpg';
import por3 from './Images/por3.jpg';
import land1 from './Images/land1.jpg';
import land2 from './Images/land2.jpg';

const AllBlogs = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [expandedBlogId, setExpandedBlogId] = useState(null);
  const [blogComments, setBlogComments] = useState({});
  const [newComment, setNewComment] = useState({ name: '', text: '', blogId: null });
  
  // Articles state
  const [articles, setArticles] = useState([]);
  const [loadingArticles, setLoadingArticles] = useState(true);
  const [totalComments, setTotalComments] = useState(0);
  const [debugInfo, setDebugInfo] = useState('');

  // Slides for the slideshow
  // const slides = [
  //   {
  //     id: 1,
  //     title: "Lisa Charity",
  //     description: "Writer",
  //     image: isMobile ? por2 : land1,
  //     color: "#8B5CF6"
  //   },
  //   {
  //     id: 2,
  //     title: "Lisa Charity",
  //     description: "Poet",
  //     image: isMobile ? por3 : land2,
  //     color: "#10B981"
  //   },
  //   {
  //     id: 3,
  //     title: "Lisa Charity",
  //     description: "Creative",
  //     image: Lisa1,
  //     color: "#3B82F6"
  //   }
  // ];

  // Detect mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load articles from Firebase
  useEffect(() => {
    const loadArticles = async () => {
      setLoadingArticles(true);
      
      try {
        // Load all articles without status filter
        console.log('Loading articles from Firebase...');
        const querySnapshot = await getDocs(collection(db, 'articles'));
        const articlesList = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          articlesList.push({
            id: doc.id,
            ...data,
            date: data.date?.toDate?.() || data.date || new Date(),
            hasImage: data.hasImage || (data.coverImage && data.coverImage.trim() !== '')
          });
        });
        
        console.log(`Found ${articlesList.length} articles`);
        setDebugInfo(`Loaded ${articlesList.length} articles from Firebase`);
        
        // Sort by date (newest first)
        articlesList.sort((a, b) => new Date(b.date) - new Date(a.date));
        
        setArticles(articlesList);
        
      } catch (error) {
        console.error('Error loading articles:', error);
        setDebugInfo(`Error: ${error.message}`);
      } finally {
        setLoadingArticles(false);
      }
    };
    
    loadArticles();
    
    // Set up real-time listener for new articles
    try {
      const articlesQuery = query(
        collection(db, 'articles'),
        orderBy('createdAt', 'desc')
      );
      
      const unsubscribe = onSnapshot(articlesQuery, (snapshot) => {
        const articlesList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate?.() || doc.data().date || new Date(),
          hasImage: doc.data().hasImage || (doc.data().coverImage && doc.data().coverImage.trim() !== '')
        }));
        
        // Sort by date
        articlesList.sort((a, b) => new Date(b.date) - new Date(a.date));
        setArticles(articlesList);
        
      }, (error) => {
        console.error('Real-time listener error:', error);
      });
      
      return () => unsubscribe();
    } catch (error) {
      console.error('Error setting up listener:', error);
    }
  }, []);

  // Load comments for articles
  useEffect(() => {
    const unsubscribeFunctions = [];
    
    articles.forEach(article => {
      try {
        const articleRef = doc(db, 'articles', article.id);
        const commentsRef = collection(articleRef, 'comments');
        const commentsQuery = query(commentsRef, orderBy('timestamp', 'desc'));
        
        const unsubscribe = onSnapshot(commentsQuery, (snapshot) => {
          const comments = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            timestamp: doc.data().timestamp?.toDate()
          }));
         
          setBlogComments(prev => {  // Wrap in an updater block
            const updatedComments = {
              ...prev,
              [article.id]: comments
            };
         
            // Compute total using the updated state
            const allComments = Object.values(updatedComments)
              .flat()
              .filter(Boolean);
            setTotalComments(allComments.length);
         
            return updatedComments;  // Return the new state
          });
        });
        
        unsubscribeFunctions.push(unsubscribe);
      } catch (error) {
        console.error(`Error setting up comments listener for article ${article.id}:`, error);
      }
    });
    
    return () => {
      unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
    };
  }, [articles]);

  const formatDate = (date) => {
    if (!date) return 'Recently';
    
    const dateObj = date instanceof Date ? date : new Date(date);
    const now = new Date();
    const diffInDays = Math.floor((now - dateObj) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatFullDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCommentDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleExpand = (blogId) => {
    if (expandedBlogId === blogId) {
      // If clicking the already expanded blog, close it
      setExpandedBlogId(null);
      setNewComment({ name: '', text: '', blogId: null });
    } else {
      // Expand this blog and collapse any others
      setExpandedBlogId(blogId);
    }
  };

  const handleCommentChange = (e, field, blogId) => {
    setNewComment(prev => ({
      ...prev,
      [field]: e.target.value,
      blogId
    }));
  };

  const handleSubmitComment = async (e, blogId) => {
    e.preventDefault();
    const { name, text } = newComment;
    
    if (text.trim()) {
      try {
        const articleRef = doc(db, 'articles', blogId);
        const commentsRef = collection(articleRef, 'comments');
        
        const commentData = {
          author: name.trim() || 'Anonymous',
          text: text.trim(),
          timestamp: serverTimestamp(),
          articleId: blogId
        };
        
        await addDoc(commentsRef, commentData);
        
        // Clear form
        setNewComment({ name: '', text: '', blogId: null });
        
        // Refresh comments
        const articleComments = blogComments[blogId] || [];
        setBlogComments(prev => ({
          ...prev,
          [blogId]: [...articleComments, { ...commentData, id: Date.now().toString() }]
        }));
        
      } catch (error) {
        console.error('Error adding comment:', error);
        alert('Failed to post comment. Please try again.');
      }
    }
  };

  const currentComments = (blogId) => blogComments[blogId] || [];
  // const isExpanded = (blogId) => expandedBlogId === blogId;

  if (loadingArticles) {
    return (
      <div className="lisachar-allblogs">
        <header className="lisachar-allblogs-header">
          <div className="lisachar-allblogs-header-content">
            <Link to="/" className="lisachar-allblogs-logo">
              LisaChar
            </Link>
            
            <nav className="lisachar-allblogs-nav">
              <Link to="/" className="lisachar-allblogs-nav-link">Home</Link>
              <Link to="/blogs" className="lisachar-allblogs-nav-link active">All Blogs</Link>
              {/* <Link to="/create" className="lisachar-allblogs-nav-link">Write</Link> */}
            </nav>
          </div>
        </header>
        
        <div className="loading-articles">
          <div className="loading-spinner"></div>
          <p>Loading articles from database...</p>
          <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>{debugInfo}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="lisachar-allblogs">
      {/* Header */}
      <header className="lisachar-allblogs-header">
        <div className="lisachar-allblogs-header-content">
          <Link to="/" className="lisachar-allblogs-logo">
            LisaChar
          </Link>
          
          <nav className="lisachar-allblogs-nav">
            <Link to="/" className="lisachar-allblogs-nav-link">Home</Link>
            <Link to="/blogs" className="lisachar-allblogs-nav-link active">All Blogs</Link>
            <Link to="/create" className="lisachar-allblogs-nav-link">Write</Link>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="lisachar-allblogs-main">
        <div className="lisachar-allblogs-container">
          {/* Page Header */}
          <div className="lisachar-allblogs-page-header">
            <h1 className="lisachar-allblogs-title">All Articles</h1>
            <p className="lisachar-allblogs-subtitle">
              Discover all our latest articles, insights, and tutorials
            </p>
            <div className="articles-stats-banner">
              <span className="stat-item">{articles.length} Articles</span>
              <span className="stat-divider">•</span>
              <span className="stat-item">{totalComments} Comments</span>
              <span className="stat-divider">•</span>
              <span className="stat-item">{debugInfo}</span>
            </div>
            {expandedBlogId && (
              <button 
                className="lisachar-allblogs-back-btn"
                onClick={() => setExpandedBlogId(null)}
              >
                ← Back to All Articles
              </button>
            )}
            {/* {!expandedBlogId && (
              <Link to="/create" className="lisachar-allblogs-create-btn">
                Write New Article
              </Link>
            )} */}
          </div>

          {/* Blog Grid */}
          {expandedBlogId ? (
            // Show only the expanded blog
            <div className="lisachar-expanded-view">
              {articles
                .filter(article => article.id === expandedBlogId)
                .map((article) => {
                  const comments = currentComments(article.id);
                  return (
                    <article key={article.id} className="lisachar-expanded-blog">
                      <div className="lisachar-expanded-header">
                        {article.hasImage && article.coverImage && (
                          <img 
                            src={article.coverImage} 
                            alt={article.title}
                            className={`lisachar-expanded-image ${
                              article.imageType === 'portrait' ? 'portrait' : 'landscape'
                            }`}
                          />
                        )}
                        <div className="lisachar-expanded-title-section">
                          {article.tags && article.tags.length > 0 && (
                            <span className="lisachar-expanded-category">{article.tags[0]}</span>
                          )}
                          <h1 className="lisachar-expanded-main-title">{article.title}</h1>
                          <div className="lisachar-expanded-meta">
                            <span className="lisachar-expanded-author">{article.author || 'Unknown Author'}</span>
                            <span className="lisachar-expanded-date">{formatFullDate(article.date)}</span>
                            <span className="lisachar-expanded-time">{article.readTime || '5 min read'}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="lisachar-expanded-content">
                        <div className="full-article-expanded">
                          <div dangerouslySetInnerHTML={{ __html: article.content }} />
                        </div>
                        
                        <div className="comments-section-expanded">
                          <h3 className="comments-title-expanded">
                            Comments ({comments.length})
                          </h3>
                          <div className="comments-list-expanded">
                            {comments.length > 0 ? (
                              comments.map((comment) => (
                                <div key={comment.id} className="comment-item-expanded">
                                  <div className="comment-author-expanded">{comment.author}</div>
                                  <div className="comment-text-expanded">{comment.text}</div>
                                  <div className="comment-date-expanded">
                                    {comment.timestamp ? formatCommentDate(comment.timestamp) : 'Recently'}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <p className="no-comments-expanded">No comments yet. Be the first to comment!</p>
                            )}
                          </div>
                          <form className="comment-form-expanded" onSubmit={(e) => handleSubmitComment(e, article.id)}>
                            <input
                              type="text"
                              placeholder="Your Name (optional)"
                              className="comment-input-expanded"
                              value={newComment.name}
                              onChange={(e) => handleCommentChange(e, 'name', article.id)}
                            />
                            <textarea
                              placeholder="Your Comment"
                              className="comment-textarea-expanded"
                              value={newComment.text}
                              onChange={(e) => handleCommentChange(e, 'text', article.id)}
                              rows="3"
                              required
                            />
                            <button type="submit" className="comment-submit-expanded">Post Comment</button>
                          </form>
                        </div>
                        
                        <button 
                          className="lisachar-expanded-close-btn" 
                          onClick={() => setExpandedBlogId(null)}
                        >
                          Close Article
                        </button>
                      </div>
                    </article>
                  );
                })}
            </div>
          ) : (
            // Show all blogs in grid view
            <>
              {articles.length === 0 ? (
                <div className="no-articles-message">
                  <h3>No articles published yet</h3>
                  <p>Be the first to write an article!</p>
                  <Link to="/create" className="lisachar-allblogs-create-btn">
                    Write Your First Article
                  </Link>
                </div>
              ) : (
                <>
                  <div className="lisachar-allblogs-grid">
                    {articles.map((article) => (
                      <article key={article.id} className="lisachar-allblogs-card">
                        <div className="lisachar-allblogs-card-wrapper">
                          {article.hasImage && article.coverImage && (
                            <div className="lisachar-allblogs-card-image-wrapper">
                              <img 
                                src={article.coverImage} 
                                alt={article.title}
                                className={`lisachar-allblogs-card-image-img ${
                                  article.imageType === 'portrait' ? 'portrait' : 'landscape'
                                }`}
                                loading="lazy"
                              />
                              {article.tags && article.tags.length > 0 && (
                                <span className="lisachar-allblogs-card-category">{article.tags[0]}</span>
                              )}
                            </div>
                          )}
                          
                          <div className="lisachar-allblogs-card-content">
                            <div className="lisachar-allblogs-card-meta">
                              <span className="lisachar-allblogs-card-author">{article.author || 'Unknown Author'}</span>
                              <span className="lisachar-allblogs-card-date">{formatDate(article.date)}</span>
                              <span className="lisachar-allblogs-card-time">{article.readTime || '5 min read'}</span>
                            </div>
                            
                            <h2 className="lisachar-allblogs-card-title">{article.title}</h2>
                            
                            <div className="lisachar-allblogs-card-excerpt">
                              {article.excerpt ? (
                                article.excerpt.split('\n').map((paragraph, index) => 
                                  paragraph.trim() && <p key={index}>{paragraph}</p>
                                )
                              ) : (
                                <p>{article.content?.substring(0, 200) + '...'}</p>
                              )}
                            </div>
                            
                            <button 
                              className="lisachar-allblogs-readmore-btn" 
                              onClick={() => toggleExpand(article.id)}
                            >
                              Read Full Article →
                            </button>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>

                  {/* Load More */}
                  <div className="lisachar-allblogs-loadmore">
                    <button className="lisachar-loadmore-btn">Load More Articles</button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="lisachar-allblogs-footer">
        <div className="lisachar-allblogs-footer-content">
          <div className="lisachar-allblogs-footer-info">
            <h3 className="lisachar-allblogs-footer-title">LisaChar</h3>
            <p className="lisachar-allblogs-footer-desc">
              Creating beautiful digital experiences through thoughtful design and development.
            </p>
          </div>
          
          <div className="lisachar-allblogs-footer-links">
            <div className="lisachar-footer-column">
              <h4>Quick Links</h4>
              <Link to="/">Home</Link>
              <Link to="/blogs">All Blogs</Link>
              <Link to="/create">Write Article</Link>
              <a href="#">Contact</a>
            </div>
          </div>
        </div>
        
        <div className="lisachar-allblogs-footer-bottom">
          <p>© 2024 LisaChar. All rights reserved.</p>
          <div className="footer-stats">
            <span>{articles.length} Articles</span>
            <span>•</span>
            <span>{totalComments} Comments</span>
          </div>
          <div className="lisachar-allblogs-social">
            <a href="#" className="lisachar-social-link">Twitter</a>
            <a href="#" className="lisachar-social-link">Instagram</a>
            <a href="#" className="lisachar-social-link">LinkedIn</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AllBlogs;